import type React from 'react';
import { useState } from 'react';
import { Modal, Button } from '@shadeables/ui';
import { useNavigate } from 'react-router-dom';
import { useSubscription, usePricing } from '@shadeables/billing';
import styles from './UpsellModal.module.css';

interface UpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UpsellModal: React.FC<UpsellModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { subscribe } = useSubscription();
  const { pricingPlans } = usePricing();
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Find the pro monthly price
  const proMonthlyPrice = pricingPlans.find((plan) =>
    plan.name.toLowerCase().includes('pro')
  )?.monthlyPrice;

  const handleUpgradeNow = async () => {
    if (!proMonthlyPrice) {
      // Fallback to billing page
      handleViewBilling();
      return;
    }

    setIsUpgrading(true);
    try {
      await subscribe(proMonthlyPrice.id);
    } catch (error) {
      console.error('Upgrade failed:', error);
      setIsUpgrading(false);
      // Could show an error message here
    }
  };

  const handleViewBilling = () => {
    onClose();
    navigate('/settings/billing');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Upgrade to Pro">
      <div className={styles.modal}>
        <div className={styles.content}>
          <div className={styles.icon}>✨</div>
          <h3 className={styles.heading}>You've reached your project limit</h3>
          <p className={styles.description}>
            Free accounts are limited to 1 project. Upgrade to Pro to create
            unlimited projects and unlock more features.
          </p>

          <div className={styles.features}>
            <div className={styles.feature}>
              <span className={styles.checkmark}>✓</span>
              <span>Unlimited projects</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.checkmark}>✓</span>
              <span>Access to 3D shaders</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.checkmark}>✓</span>
              <span>Advanced shader features</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.checkmark}>✓</span>
              <span>Priority support</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.checkmark}>✓</span>
              <span>Export to multiple formats</span>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <Button variant="ghost" onClick={onClose}>
            Stay Free
          </Button>
          <Button onClick={handleUpgradeNow} disabled={isUpgrading}>
            {isUpgrading ? 'Processing...' : 'Upgrade to Pro'}
          </Button>
          <Button variant="ghost" onClick={handleViewBilling}>
            View All Plans
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UpsellModal;
